import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import {
  initStageReport,
  fetchGameStages,
  updateStageProgress,
  submitCanvasTile,
  startCanvasActivity,
  endCanvasActivity,
} from '../../functions/stageReport';
import {
  reconcileOnLoad,
  isSessionEndedError,
  withRetry,
  expirySpreadMs,
  randomDelayMs,
  createSingleFlight,
  finalizeThenComplete,
  submitThenFinalize,
} from '../../utils/canvasCompletion';
import { fetchReport } from '../../functions/fetchReport';
import { sendReport, buildReportIdentity } from '../../functions/sendReport';
import { getPuzzleThemeConfig } from '../../utils/puzzleTheme';
import { readStoredUserData } from '../../utils/gameStages';
import { computePuzzleGrid } from '../../utils/puzzleGrid';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  buildFirebaseSessionId,
  sanitizeFirebaseKey,
  registerUserCanvasSession,
  patchUserCanvasState,
  ensureCanvasActivityStarted,
  writeLivePreview,
  clearLivePreview,
} from '../../utils/canvasFirebase';
import { subSliceRect, isSliced } from '../../utils/subSlots';
import { cropImageToRect } from '../../utils/cropImage';
import { formatGameTimer } from '../../utils/gameTimer';
import { pushHistory } from '../../utils/canvasHistory';
import {
  buildCumulativeStageTimes,
  buildCumulativeStageScore,
  readCumulativeScoreFromProgress,
  readCumulativeTimeFromProgress,
} from '../../utils/stageReportTime';
import { floodFillComposite, rgbDistanceSq } from './floodFill';
import ColorSwatchRow from '../../components/ColorSwatchRow/ColorSwatchRow';
import LiveCanvasBoard from '../../components/LiveCanvasBoard/LiveCanvasBoard';
import { fetchAssetAsObjectUrl } from '../../utils/themeAsset';
import './canvas.css';

const DEFAULT_TIMER_SECONDS = 5 * 60;

const PARTICIPATION_POINTS = 1;
const PREVIEW_MIN_INTERVAL_MS = 3000;
// Matches TARGET_CANVAS (the drawing canvas's own resolution cap) below — no point
// downscaling further, since the board tile displays this at a size that can exceed
// 220px and a smaller source just gets blurry when the browser stretches it back up.
const PREVIEW_MAX_DIM = 240;
const PREVIEW_JPEG_QUALITY = 0.6;

const PENCIL_SIZE = 3;
const PENCIL_OPACITY = 0.9;
const BRUSH_SIZE_MAX = 24;
const ERASER_SIZE_MAX = 40;
const TARGET_CANVAS = 520;
const MIN_CANVAS = 64;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 6;
const ZOOM_STEP = 0.25;

const FILL_COLOR_TOLERANCE = 62;
const FILL_SKIP_MAX_DIST = 22;
const FILL_SKIP_DIST_SQ = FILL_SKIP_MAX_DIST * FILL_SKIP_MAX_DIST;

// Persists the in-progress drawing across a page refresh — sessionStorage so a
// genuinely new session (new tab/window) doesn't inherit someone else's leftover
// scratch work, but a reload of the same tab restores exactly where they left off.
function canvasSnapshotKey(userId, sessionId, puzzleId) {
  return `bigpicture_canvas_snapshot_${userId}_${sessionId}_${puzzleId}`;
}

function saveCanvasSnapshot(key, dataUrl) {
  try {
    sessionStorage.setItem(key, dataUrl);
  } catch {
    /* ignore — quota exceeded, private browsing, etc.; worst case is losing
       restore-on-refresh, not the current in-memory drawing */
  }
}

function loadCanvasSnapshot(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function clearCanvasSnapshot(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

function strokeWidthForTool(tool, brushSize, eraserSize) {
  if (tool === 'eraser') return eraserSize;
  if (tool === 'pencil') return PENCIL_SIZE;
  return brushSize;
}

/**
 * A ring cursor sized to the actual on-screen stroke diameter — the classic
 * paint-app "brush outline follows your pointer" cursor — so you can see
 * exactly how wide a stroke will land before you draw it. White outer ring +
 * black inner ring keeps it visible over both light and dark artwork.
 */
const CURSOR_MAX_DIAMETER = 128; // custom cursor images beyond this render inconsistently across browsers

function buildBrushCursor(diameterCss) {
  const d = Math.max(2, Math.min(CURSOR_MAX_DIAMETER, Math.round(diameterCss)));
  const pad = 2;
  const size = d + pad * 2;
  const c = size / 2;
  const r = d / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
    `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="white" stroke-width="3"/>` +
    `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="black" stroke-width="1.25"/>` +
    `</svg>`;
  const base64 = typeof window !== 'undefined' ? window.btoa(svg) : '';
  return `url("data:image/svg+xml;base64,${base64}") ${c} ${c}, crosshair`;
}

function ClockIcon() {
  return (
    <svg className="canvas-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25ZM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z"
      />
    </svg>
  );
}

function BrushIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M7 21q-1.25 0-2.125-.875T4 18q0-1.25.875-2.125T7 15h.75q.325 0 .563-.238T8.55 14.2q0-.35-.25-.6t-.6-.25H7q-.425 0-.713-.288T6 12.35q0-.425.288-.713T7 11.35h.7q.325 0 .563-.238T8.5 10.55q0-.35-.25-.6t-.6-.25H7q-1.65 0-2.825-1.175T3 5.7q0-.85.313-1.6T4.2 2.8l1.4 1.4q-.35.35-.475.788T5 6q0 .825.588 1.413T7 8h.65q1.15 0 1.975.825T10.45 10.8q0 .375-.113.725t-.312.65l3.575 3.575q.3-.2.65-.313t.725-.112q1.15 0 1.975.825t.825 1.975q0 1.65-1.175 2.825T14 21H7Z"
      />
    </svg>
  );
}

function FillIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="m19 11.5-6.5-6.5-1.42 1.42 1.13 1.13-4.7 4.7a1.5 1.5 0 0 0 0 2.12l4.5 4.5a1.5 1.5 0 0 0 2.12 0l4.7-4.7a1.5 1.5 0 0 0 .17-1.67ZM8.79 12 12 8.79 15.21 12ZM3 21h13v-2H3v2Z"
      />
    </svg>
  );
}

function EraserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M16.24 3.56 21.19 8.5a2 2 0 0 1 0 2.83l-8.66 8.66a2 2 0 0 1-2.83 0L3.56 13.87a2 2 0 0 1 0-2.83l8.85-8.85a2 2 0 0 1 2.83.37ZM6.5 18.5H21v2H8.5Z"
      />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.1 0 2-.9 2-2 0-.51-.2-.98-.52-1.32-.3-.32-.48-.76-.48-1.24 0-1 .81-1.81 1.81-1.81H17c2.76 0 5-2.24 5-5C22 6.03 17.53 2 12 2Zm-5.5 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3ZM9 6.5A1.5 1.5 0 1 1 9 9.5 1.5 1.5 0 0 1 9 6.5Zm6 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm2.5 4.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"
      />
    </svg>
  );
}

export default function Canvas() {
  const navigate = useNavigate();
  const themeData = useSelector((state) => state.theme.data);
  const currentTheme = useSelector((state) => state.theme.currentTheme);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const timerStartRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const initCalledRef = useRef(false);
  const completingRef = useRef(false);
  const autoSubmitRef = useRef(false);
  const submitOnceRef = useRef(false);
  // True once the tile image itself has been uploaded and marked 'submitted'
  // server-side — that step can't be retried (the backend rejects a second
  // upload for an already-submitted tile), so if the *next* step
  // (saveGameReport) fails, a retry must skip straight back to that step
  // instead of re-uploading.
  const tileUploadedRef = useRef(false);
  const [reportSaveFailed, setReportSaveFailed] = useState(false);
  // Which step a manual "Retry" must re-run: 'complete' (tile saved, only the
  // completion record is missing) or 'blank' (timer expired on an undrawn
  // canvas and the finalize / completion step failed — nothing to upload).
  const retryModeRef = useRef(null);
  const runBlankCompletionRef = useRef(null);
  const saveGameReportRef = useRef(null);
  const finalizeFlightRef = useRef(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const firebaseRef = useRef({ sessionId: null, userId: null });
  const lastPreviewPushRef = useRef(0);
  const pushLivePreviewRef = useRef(null);
  const previewTrailingTimeoutRef = useRef(null);
  // devicePixelRatio the canvas's backing store was last sized for — the canvas's
  // *internal resolution* (canvas.width/height) is rendered at this multiple of its
  // on-screen CSS size, so strokes are drawn at the display's real pixel density
  // instead of being upscaled and blurred. canvasCoords() and applyFill() need this
  // to convert screen coordinates into that higher-resolution pixel space.
  const dprRef = useRef(1);
  // Set once puzzleId/user are known (see fetchCanvasData) so commitState can save
  // a restore-on-refresh snapshot under a key unique to this player's assigned tile.
  const snapshotKeyRef = useRef(null);
  // True once a saved snapshot has actually been restored onto the canvas — the
  // restore resets historyIndexRef to 0 (the new baseline), which is otherwise
  // indistinguishable from "nothing was ever drawn" (also index 0) and would
  // make an auto-submit on timer expiry wrongly skip a real, restored drawing.
  const hasRestoredSnapshotRef = useRef(false);
  const handleTimerExpiredRef = useRef(null);
  const expirySpreadMsRef = useRef(expirySpreadMs(null));
  const handleSubmitRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [puzzleId, setPuzzleId] = useState(null);
  const [outlineUrl, setOutlineUrl] = useState('');
  const [sliceUrls, setSliceUrls] = useState([]);
  const [gameMode, setGameMode] = useState('self_paced');
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_TIMER_SECONDS);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [ready, setReady] = useState(false);
  const [baseSize, setBaseSize] = useState(null);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const baseSizeRef = useRef(null);
  zoomRef.current = zoom;
  baseSizeRef.current = baseSize;
  const [historyUi, setHistoryUi] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tool, setTool] = useState('brush');
  const [color, setColor] = useState('#e63946');
  const [brushSize, setBrushSize] = useState(8);
  const [eraserSize, setEraserSize] = useState(16);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);

  const title =
    String(themeData?.landing_page_title4 ?? themeData?.landing_title ?? 'Collaborative Canvas').trim() ||
    'Collaborative Canvas';
  const titleColor = themeData?.landing_page_title_color ?? themeData?.text_color ?? null;
  const buttonBg = themeData?.landing_page_button_bgcolor ?? themeData?.box_bg_color ?? null;
  const accentColor = themeData?.button_color ?? themeData?.box_bg_color ?? null;

  const rules = useMemo(() => {
    if (!Array.isArray(themeData?.rules)) return [];
    return themeData.rules.map((r) => String(r ?? '').trim()).filter(Boolean);
  }, [themeData?.rules]);

  const rulesModalBg = useMemo(() => {
    const raw =
      window.innerWidth <= 768 ? themeData?.background_mob : themeData?.background_desk;
    return raw ? s3PublicUrl(raw) : '';
  }, [themeData?.background_desk, themeData?.background_mob]);

  const pageStyle = useMemo(
    () => ({
      '--canvas-title-color': titleColor || undefined,
      '--canvas-accent': accentColor || buttonBg || undefined,
      '--canvas-pill-border': buttonBg || undefined,
    }),
    [titleColor, buttonBg, accentColor]
  );

  const { macroRows, macroCols } = useMemo(() => {
    const count =
      sliceUrls.length ||
      Math.floor(Number(themeData?.total_puzzles ?? themeData?.puzzle_parts)) ||
      9;
    const savedRows = Math.floor(Number(themeData?.row));
    const savedCols = Math.floor(Number(themeData?.col));
    if (
      Number.isFinite(savedRows) &&
      Number.isFinite(savedCols) &&
      savedRows * savedCols === count
    ) {
      return { macroRows: savedRows, macroCols: savedCols };
    }
    const grid = computePuzzleGrid(count);
    return { macroRows: grid.rows, macroCols: grid.cols };
  }, [sliceUrls.length, themeData?.row, themeData?.col, themeData?.total_puzzles, themeData?.puzzle_parts]);

  const timerDurationSeconds = useMemo(() => {
    const minutes = Number(themeData?.game_timer_minutes);
    const seconds = Number(themeData?.game_timer_seconds);
    const validMinutes = Number.isFinite(minutes) && minutes >= 0 && minutes <= 30 ? minutes : null;
    const validSeconds = Number.isFinite(seconds) && seconds >= 0 && seconds <= 59 ? seconds : null;
    if (validMinutes == null && validSeconds == null) return DEFAULT_TIMER_SECONDS;
    const total = (validMinutes ?? 0) * 60 + (validSeconds ?? 0);
    return total > 0 ? total : DEFAULT_TIMER_SECONDS;
  }, [themeData?.game_timer_minutes, themeData?.game_timer_seconds]);

  const countdownDisplay = timerReady
    ? formatGameTimer(secondsLeft)
    : formatGameTimer(timerDurationSeconds);

  const locked = gameMode === 'live' && timerExpired;
  const interactionLocked = submitting || submitted || locked;

  const getRemainingSeconds = useCallback(() => {
    if (!timerStartRef.current) return timerDurationSeconds;
    const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
    return Math.max(0, timerDurationSeconds - elapsed);
  }, [timerDurationSeconds]);

  const getElapsedSeconds = useCallback(() => {
    if (!timerStartRef.current) return 0;
    const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
    if (gameMode === 'live') {
      return Math.min(timerDurationSeconds, Math.max(0, elapsed));
    }
    return Math.max(0, elapsed);
  }, [gameMode, timerDurationSeconds]);

  // ---- Assignment + timer + Firebase wiring (mirrors puzzle.jsx) ----
  useEffect(() => {
    if (initCalledRef.current) return;
    if (!themeData) return;
    initCalledRef.current = true;

    const fetchCanvasData = async () => {
      const userId = user?.userId;
      const organizationId = user?.organizationId;
      const sessionId = user?.sessionId;
      const token =
        adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

      if (!token || !userId || !organizationId || !sessionId) {
        setMessage('Unable to load your canvas. Please sign in again.');
        setLoading(false);
        return;
      }

      try {
        // Live mode: the first caller starts the server clock AND freezes how each
        // piece is divided among its players (sub-slots) — this must happen before
        // this player's assignment is read below, or they could draw the whole
        // piece while everyone else draws a slice of it. Idempotent for the rest.
        let serverClock = null;
        // Skew (server clock - this browser's clock) is measured the moment the reply
        // arrives, not later, so the seconds spent loading the rest don't leak into it.
        let clockSkewMs = 0;
        if (String(themeData?.game_mode ?? '').trim().toLowerCase() === 'live') {
          try {
            serverClock = await startCanvasActivity({ token, userId, organizationId, sessionId, currentTheme });
            clockSkewMs = Number(serverClock.serverNow) - Date.now();
            expirySpreadMsRef.current = expirySpreadMs(serverClock.graceSeconds);
          } catch (clockErr) {
            console.warn('Could not read the server activity clock; using the shared Firebase clock:', clockErr);
          }
        }

        const [stageData, reportResp] = await Promise.all([
          fetchGameStages({ token, userId, organizationId, sessionId }),
          fetchReport({ userId, token, sessionId, organizationId }),
        ]);
        const game = stageData?.game ?? null;
        const report = reportResp?.report ?? null;

        const reportStatus = String(report?.status ?? '').trim().toLowerCase();
        if (reportStatus === 'completed') {
          navigate('/canvas-reveal', { replace: true });
          return;
        }

        const stored = readStoredUserData();
        let resolvedPuzzleId = game?.puzzle_id ?? stored?.puzzle_id ?? stored?.puzzleid ?? null;

        const data = await initStageReport({
          token,
          userId,
          email: user?.email,
          name: user?.name,
          userType: user?.role || user?.source || 'ORG_USER',
          reportId: user?.reportId || '',
          themeName: 'Collaborative Canvas',
          totalPuzzles: themeData?.total_puzzles ?? themeData?.puzzle_parts ?? 9,
          currentTheme,
        });

        resolvedPuzzleId =
          resolvedPuzzleId ??
          data?.game?.stages?.puzzle_id ??
          data?.game?.puzzle_id ??
          null;

        // R2-P07: reconcile tile vs report status from the server on every load.
        // 'complete_only' = the tile was already saved (or auto-filled) but the
        // completion record is missing — never redraw/re-upload, only re-run the
        // idempotent completion below.
        const reconcile = reconcileOnLoad({
          tileStatus: data?.game?.tile_status ?? game?.tile_status,
          reportStatus: data?.report_status ?? reportStatus,
        });
        if (reconcile === 'reveal') {
          navigate('/canvas-reveal', { replace: true });
          return;
        }

        const themeConfig = getPuzzleThemeConfig(themeData, resolvedPuzzleId);
        const slices = themeConfig.slices ?? [];
        const idNum = Number(resolvedPuzzleId);
        const outline =
          Number.isFinite(idNum) && idNum >= 1 ? slices[idNum - 1] : null;

        setPuzzleId(resolvedPuzzleId);
        setSliceUrls(slices);
        snapshotKeyRef.current =
          resolvedPuzzleId != null ? canvasSnapshotKey(userId, sessionId, resolvedPuzzleId) : null;

        // When players outnumber pieces this piece is DIVIDED among its players
        // (server-frozen sub_index of sub_count): draw only this player's slice.
        const subIndex = data?.game?.sub_index ?? game?.sub_index ?? null;
        const subCount = data?.game?.sub_count ?? game?.sub_count ?? null;
        const sliced = isSliced(subCount);

        const rawOutline = outline || themeConfig.image || '';
        const wholeOutline = await fetchAssetAsObjectUrl(rawOutline, token);
        const resolvedOutline = sliced
          ? await cropImageToRect(wholeOutline, subSliceRect(subIndex, subCount))
          : wholeOutline;
        setOutlineUrl(resolvedOutline);

        const modeRaw = String(themeData?.game_mode ?? '').trim().toLowerCase();
        const resolvedMode = modeRaw === 'live' ? 'live' : 'self_paced';
        setGameMode(resolvedMode);

        const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);
        const firebaseUserId = sanitizeFirebaseKey(userId);
        firebaseRef.current = { sessionId: firebaseSessionId, userId: firebaseUserId };
        // Self-Paced mode has no *live* board while drawing, but the final reveal
        // page (both modes) still reads each tile's finished image from this same
        // Firebase record — skipping this write for self-paced meant a self-paced
        // player's submitted painting never had anywhere to be looked up from, so
        // the reveal always fell back to the blank/master image for their tile.
        if (resolvedPuzzleId != null) {
          registerUserCanvasSession(firebaseSessionId, firebaseUserId, {
            userId: String(userId),
            name: user?.name || user?.email || 'Player',
            puzzle_id: Number(resolvedPuzzleId),
            sub_index: sliced ? Number(subIndex) : null,
            sub_count: sliced ? Number(subCount) : null,
            status: 'assigned',
          }).catch((err) => console.warn('Failed to register canvas session:', err));
        }

        if (resolvedMode === 'live') {
          // A single shared clock for the whole session's drawing phase — only the
          // *start moment* is seeded once (by whichever player reaches the canvas
          // first) and shared, so everyone counts down from the same startedAt. The
          // *duration* is always read fresh from the current admin timer setting
          // (timerDurationSeconds), never from whatever was seeded into Firebase —
          // otherwise an admin changing the minutes/seconds later has no effect on
          // a session that already seeded a clock, which is exactly the bug where
          // an edited 15-minute setting kept counting down from an earlier value.
          //
          // The start moment is now stamped by the SERVER (startCanvasActivity)
          // so the backend can authorize finalize/join from its own clock; it is
          // converted into this browser's clock frame via the reported skew, and
          // mirrored into Firebase for the other consumers of that node. If the
          // server clock can't be reached we fall back to the Firebase clock.
          const serverStartedAt = serverClock ? Number(serverClock.startedAt) - clockSkewMs : null;
          const activity = await ensureCanvasActivityStarted(
            firebaseSessionId,
            timerDurationSeconds,
            serverStartedAt
          );
          const sharedStartedAt = serverStartedAt ?? (Number(activity?.startedAt) || Date.now());
          timerStartRef.current = sharedStartedAt;
          const remaining = serverClock?.ended
            ? 0
            : Math.max(0, timerDurationSeconds - Math.floor((Date.now() - sharedStartedAt) / 1000));

          if (remaining <= 0 && reconcile !== 'complete_only') {
            const hasSnapshot = Boolean(
              snapshotKeyRef.current && loadCanvasSnapshot(snapshotKeyRef.current)
            );
            if (!hasSnapshot) {
              // This session's drawing phase already fully elapsed before this player
              // ever drew anything (e.g. joined very late) — nothing to submit, so
              // send them straight to the reveal instead of a locked/pointless canvas.
              setMessage('Time is up. This tile will be auto-filled.');
              autoSubmitRef.current = true;
              submitOnceRef.current = true;
              // R2-N01: auto-fill must complete BEFORE progress is saved, and a
              // failure here must not navigate as if it had succeeded.
              const outcome = await runBlankCompletionRef.current?.(resolvedPuzzleId);
              if (outcome?.ok) {
                setSubmitted(true);
                navigate('/canvas-reveal', { replace: true });
                return;
              }
              setSecondsLeft(0);
              setTimerExpired(true);
              setTimerReady(true);
              return;
            }
            // A snapshot exists — this player HAD been drawing (e.g. they refreshed
            // right as the countdown hit zero) and that work must actually be
            // submitted, not discarded as "nothing drawn". Let the canvas mount
            // normally so the outline image loads and restoreSavedDrawing can put
            // it back, and mark the timer already-expired so the auto-submit effect
            // picks it up the moment it's ready (see the `ready` guard below).
          }

          setSecondsLeft(remaining);
          setTimerExpired(remaining <= 0);
        }
        setTimerReady(true);

        if (reconcile === 'complete_only') {
          tileUploadedRef.current = true;
          submitOnceRef.current = true;
          autoSubmitRef.current = true;
          completingRef.current = true;
          const saved = await saveGameReportRef.current?.(resolvedPuzzleId);
          if (saved) {
            if (snapshotKeyRef.current) clearCanvasSnapshot(snapshotKeyRef.current);
            setSubmitted(true);
            navigate('/canvas-reveal', { replace: true });
            return;
          }
          completingRef.current = false;
          retryModeRef.current = 'complete';
          setReportSaveFailed(true);
          setMessage(
            'Your artwork was saved, but we could not record your completion. Tap Retry to try again.'
          );
        }
      } catch (err) {
        if (isSessionEndedError(err)) {
          // R2-P08: the activity is over and no tile may be allocated.
          setSessionEnded(true);
          setMessage(err.message || 'This activity has already ended.');
        } else {
          setMessage(err?.message || 'Failed to load canvas configuration.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCanvasData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, user, themeData, navigate]);

  // ---- Live-mode countdown ticking ----
  useEffect(() => {
    if (loading || gameMode !== 'live' || !timerReady || !timerStartRef.current || timerExpired) {
      return undefined;
    }

    const tick = () => {
      const remaining = getRemainingSeconds();
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setTimerExpired(true);
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    };

    tick();
    timerIntervalRef.current = window.setInterval(tick, 1000);

    return () => {
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [loading, gameMode, timerReady, timerExpired, getRemainingSeconds]);

  // ---- Drawing engine (ported from split_canvas/DrawPage.jsx) ----
  const canvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const dpr = dprRef.current || 1;
    // canvas.width/height are the backing store's raw pixels (dpr× the logical
    // size); ctx has a matching setTransform(dpr, ...), so every drawing call
    // (moveTo/lineTo/getImageData's *coordinates*, not its buffer) expects
    // logical coordinates — dividing canvas.width by dpr here converts back to
    // that logical space instead of the raw, dpr-scaled pixel space.
    const scaleX = canvas.width / dpr / rect.width;
    const scaleY = canvas.height / dpr / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // Streams a small preview of this player's in-progress tile to Firebase so
  // the Live Collaborative Artwork board can show their strokes as they draw,
  // not just once they submit. Throttled + downscaled to keep RTDB writes and
  // payload size small; best-effort (self-paced mode has no board to feed).
  // A skipped call schedules a trailing run so the last stroke in a fast burst
  // (e.g. quick undo/redo clicks) still lands instead of being dropped forever.
  const pushLivePreview = useCallback(() => {
    if (gameMode !== 'live') return;
    const { sessionId: fbSessionId, userId: fbUserId } = firebaseRef.current;
    if (!fbSessionId || !fbUserId) return;

    const now = Date.now();
    const elapsed = now - lastPreviewPushRef.current;
    if (elapsed < PREVIEW_MIN_INTERVAL_MS) {
      if (!previewTrailingTimeoutRef.current) {
        previewTrailingTimeoutRef.current = setTimeout(() => {
          previewTrailingTimeoutRef.current = null;
          pushLivePreview();
        }, PREVIEW_MIN_INTERVAL_MS - elapsed);
      }
      return;
    }
    lastPreviewPushRef.current = now;

    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img?.complete || !canvas.width || !canvas.height) return;

    const scale = Math.min(1, PREVIEW_MAX_DIM / Math.max(canvas.width, canvas.height));
    const w = Math.max(1, Math.round(canvas.width * scale));
    const h = Math.max(1, Math.round(canvas.height * scale));

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, w, h);
    ctx.drawImage(canvas, 0, 0, w, h);

    let dataUrl;
    try {
      dataUrl = out.toDataURL('image/jpeg', PREVIEW_JPEG_QUALITY);
    } catch {
      return;
    } finally {
      // Release the backing store now; Safari otherwise holds it until GC and a new
      // canvas is created for every preview.
      out.width = 0;
      out.height = 0;
    }

    writeLivePreview(fbSessionId, fbUserId, dataUrl).catch((err) =>
      console.warn('Failed to push live canvas preview:', err)
    );
  }, [gameMode]);

  pushLivePreviewRef.current = pushLivePreview;

  // Best-effort: persist whatever the canvas currently shows, keyed to this
  // player's assigned tile, so a refresh restores it instead of starting blank.
  const saveSnapshotNow = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !snapshotKeyRef.current) return;
    try {
      saveCanvasSnapshot(snapshotKeyRef.current, canvas.toDataURL('image/png'));
    } catch {
      /* ignore — e.g. a tainted canvas; best-effort persistence only */
    }
  }, []);

  const commitState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const snap = ctx.getImageData(0, 0, width, height);
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    const next = pushHistory(hist, idx, snap);
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
    setHistoryUi((n) => n + 1);
    pushLivePreview();
    saveSnapshotNow();
  }, [pushLivePreview, saveSnapshotNow]);

  // Sized to match the tile image's actual aspect ratio (never forced square) so the
  // drawing canvas, the visible <img> box, and the offscreen fill-sampling composite
  // all agree on where the image content sits — a forced square + object-fit:contain
  // would letterbox the visible image while drawImage() stretches it when sampling,
  // causing colors to land slightly off from the outline they're meant to fill.
  const layoutCanvasSquare = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!wrap || !canvas || !img?.complete || !img.naturalWidth || !img.naturalHeight) return;

    // While zoomed, keep the base (100%) fit locked. Re-fitting after a scrollbar
    // appears used to shrink the base width and cancel horizontal overflow — the
    // classic "vertical scroll works, horizontal never engages" loop.
    if (zoomRef.current !== 1 && baseSizeRef.current) return;

    const style = getComputedStyle(wrap);
    const pl = parseFloat(style.paddingLeft) || 0;
    const pr = parseFloat(style.paddingRight) || 0;
    const pt = parseFloat(style.paddingTop) || 0;
    const pb = parseFloat(style.paddingBottom) || 0;
    // Outer box size (not clientWidth) so classic scrollbars don't feed back into fit.
    const rect = wrap.getBoundingClientRect();
    const contentW = rect.width - pl - pr;
    const contentH = rect.height - pt - pb;

    const aspect = img.naturalWidth / img.naturalHeight;
    const maxW = contentW > 0 ? Math.min(contentW, TARGET_CANVAS) : TARGET_CANVAS;
    const maxH = contentH > 0 ? Math.min(contentH, TARGET_CANVAS) : TARGET_CANVAS;

    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    if (w < MIN_CANVAS || h < MIN_CANVAS) {
      const scaleUp = MIN_CANVAS / Math.min(w, h);
      w *= scaleUp;
      h *= scaleUp;
    }

    const cw = Math.round(w);
    const ch = Math.round(h);
    // Render at the display's actual pixel density (devicePixelRatio), not just
    // cw×ch CSS pixels — otherwise on a >1x-DPR screen (virtually all phones) the
    // canvas's backing store has fewer pixels than the screen packs into that
    // space, so the browser upscales it and every stroke (pencil most visibly,
    // being the thinnest) comes out soft/blurry. baseSize (cw, ch) stays the
    // logical/CSS size — only the backing store and coordinate math below scale
    // by dpr, so the element's on-screen size and all drawing logic are unaffected.
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const pw = Math.round(cw * dpr);
    const ph = Math.round(ch * dpr);
    if (canvas.width === pw && canvas.height === ph) {
      setBaseSize((prev) => (prev?.cw === cw && prev?.ch === ch ? prev : { cw, ch }));
      return;
    }

    // Reassigning canvas.width/height always wipes the backing store — a window
    // resize or phone rotation that changes the fitted box's pixel dimensions
    // used to silently erase every stroke and reset undo to a blank page.
    // Preserve whatever's currently drawn by copying it to an offscreen canvas
    // first, then redraw it (rescaled to the new size) once the backing store
    // is resized, instead of just clearing straight to blank.
    const hadPriorContent = canvas.width > 0 && canvas.height > 0 && historyRef.current.length > 0;
    let preserved = null;
    if (hadPriorContent) {
      const offscreen = document.createElement('canvas');
      offscreen.width = canvas.width;
      offscreen.height = canvas.height;
      offscreen.getContext('2d').drawImage(canvas, 0, 0);
      preserved = offscreen;
    }

    canvas.width = pw;
    canvas.height = ph;
    dprRef.current = dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    if (preserved) {
      ctx.drawImage(preserved, 0, 0, preserved.width, preserved.height, 0, 0, cw, ch);
    }
    historyRef.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
    historyIndexRef.current = 0;
    setHistoryUi((n) => n + 1);
    setBaseSize({ cw, ch });
  }, []);

  // Revoke the previous object URL whenever it's replaced or the page unmounts.
  useEffect(() => {
    return () => {
      if (typeof outlineUrl === 'string' && outlineUrl.startsWith('blob:')) {
        URL.revokeObjectURL(outlineUrl);
      }
    };
  }, [outlineUrl]);

  useEffect(() => {
    return () => {
      if (previewTrailingTimeoutRef.current) {
        clearTimeout(previewTrailingTimeoutRef.current);
      }
    };
  }, []);

  // Draws a snapshot saved earlier in this tab (see saveSnapshotNow) back onto the
  // canvas and makes it the new base of the undo history — the restored drawing
  // just *appears*, as if it had always been there, rather than being one more
  // undo-able step back to a blank canvas. Runs right after layoutCanvasSquare
  // resets the canvas to blank, so a refresh doesn't leave the player staring at
  // an empty tile they'd already started painting.
  // Returns a Promise that resolves once restoration has actually finished (or
  // immediately if there's nothing to restore) — the caller awaits it before
  // flipping `ready`. It used to fire-and-forget: `setReady(true)` ran right
  // after calling this, while the actual restore only landed later in
  // Image.onload. If the live timer expired in that gap, handleSubmit's
  // "nothing drawn, skip upload" check (historyIndexRef<=0 &&
  // !hasRestoredSnapshotRef) could run before hasRestoredSnapshotRef flipped
  // true, discarding a real saved drawing that was a few milliseconds from
  // being restored.
  const restoreSavedDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    const key = snapshotKeyRef.current;
    if (!canvas || !key) return Promise.resolve();
    const dataUrl = loadCanvasSnapshot(key);
    if (!dataUrl) return Promise.resolve();
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        const dpr = dprRef.current || 1;
        const cw = canvas.width / dpr;
        const ch = canvas.height / dpr;
        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
        historyRef.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
        historyIndexRef.current = 0;
        hasRestoredSnapshotRef.current = true;
        setHistoryUi((n) => n + 1);
        // A refresh must not blank this player's tile on everyone else's live
        // board: re-publish the restored drawing right away.
        pushLivePreviewRef.current?.();
        resolve();
      };
      // Don't leave `ready` stuck false forever over a corrupt/unloadable snapshot.
      img.onerror = () => resolve();
      img.src = dataUrl;
    });
  }, []);

  const handleBaseImgLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      imgRef.current = img;
      const wrap = wrapRef.current;
      if (!wrap || !img.naturalWidth) return;
      const run = () => {
        layoutCanvasSquare();
        restoreSavedDrawing().finally(() => setReady(true));
      };
      if (wrap.clientWidth === 0) {
        requestAnimationFrame(run);
        return;
      }
      run();
    },
    [layoutCanvasSquare, restoreSavedDrawing]
  );

  const handleBaseImgError = useCallback(() => {
    setMessage('Failed to load your tile image. Please refresh the page.');
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      layoutCanvasSquare();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [layoutCanvasSquare]);

  const applyFill = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (!canvas || !img?.complete || !ready) return;
      const w = canvas.width;
      const ch = canvas.height;
      if (w <= 0 || ch <= 0) return;

      const { x, y } = canvasCoords(e);
      const dpr = dprRef.current || 1;
      // canvasCoords() returns logical coordinates, but w/ch (canvas.width/height)
      // are the dpr-scaled backing-store pixels the composite below samples in —
      // scale the seed point into that same raw pixel space.
      const sx = Math.max(0, Math.min(w - 1, Math.floor(x * dpr)));
      const sy = Math.max(0, Math.min(ch - 1, Math.floor(y * dpr)));

      const tmp = document.createElement('canvas');
      tmp.width = w;
      tmp.height = ch;
      const tx = tmp.getContext('2d');
      if (!tx) return;
      // Nearest-neighbor (no smoothing) when downscaling the outline image so thin
      // line-art strokes stay fully dark instead of being blurred into light gray —
      // blended lines fall under the ink-detection threshold and let fill color leak
      // straight through them.
      tx.imageSmoothingEnabled = false;
      tx.drawImage(img, 0, 0, w, ch);
      tx.drawImage(canvas, 0, 0);
      const composite = tx.getImageData(0, 0, w, ch);

      // Dilation amounts are in raw pixels — scale by dpr so the same *visual*
      // gap-bridging/fringe distance applies regardless of screen pixel density.
      // One CSS pixel: closes hairline gaps in a stroke without also walling off
      // small petals, leaf tips and narrow necks (2 CSS px per side did).
      const dilatePx = Math.max(1, Math.round(dpr));
      const { mask, count, target } = floodFillComposite(composite, sx, sy, {
        colorTolerance: FILL_COLOR_TOLERANCE,
        barrierDilate: dilatePx,
        // Fill mask must grow back out at least as far as the wall was dilated
        // inward, or a ring of unfilled (near-white) anti-aliased pixels is left
        // between the paint and the outline it's supposed to butt up against.
        fringeGrow: dilatePx,
        // More lenient than the 62 default: nearest-neighbor sampling of the
        // (often much higher-res) outline image into the canvas's resolution can
        // reduce a thin line to a partial-darkness remnant rather than full black,
        // and a stricter threshold let those spots silently drop out of the wall —
        // this showed up as fill leaking past the line on some edges, worse on
        // small/thin elements where the whole outline is thin lines.
        outlineLumSoft: 100,
      });
      if (!target || count === 0) return;

      const { r: fr, g: fg, b: fb } = hexToRgb(color);
      if (
        rgbDistanceSq(fr, fg, fb, target.r, target.g, target.b) <= FILL_SKIP_DIST_SQ
      ) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const overlayData = ctx.getImageData(0, 0, w, ch);
      const od = overlayData.data;
      for (let i = 0; i < mask.length; i++) {
        if (!mask[i]) continue;
        const p = i * 4;
        od[p] = fr;
        od[p + 1] = fg;
        od[p + 2] = fb;
        od[p + 3] = 255;
      }
      ctx.putImageData(overlayData, 0, 0);
      commitState();
    },
    [ready, canvasCoords, color, commitState]
  );

  const restoreHistoryIndex = useCallback((i) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const snap = historyRef.current[i];
    if (!snap) return;
    ctx.putImageData(snap, 0, 0);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    restoreHistoryIndex(historyIndexRef.current);
    setHistoryUi((n) => n + 1);
    pushLivePreview();
    saveSnapshotNow();
  }, [restoreHistoryIndex, pushLivePreview, saveSnapshotNow]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    restoreHistoryIndex(historyIndexRef.current);
    setHistoryUi((n) => n + 1);
    pushLivePreview();
    saveSnapshotNow();
  }, [restoreHistoryIndex, pushLivePreview, saveSnapshotNow]);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  const applyStrokeStyle = useCallback(
    (ctx, activeTool) => {
      const w = strokeWidthForTool(activeTool, brushSize, eraserSize);
      if (activeTool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.globalAlpha = 1;
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = activeTool === 'pencil' ? PENCIL_OPACITY : 1;
        ctx.strokeStyle = color;
      }
      ctx.lineWidth = w;
      ctx.lineCap = activeTool === 'pencil' ? 'butt' : 'round';
      ctx.lineJoin = 'round';
    },
    [brushSize, eraserSize, color]
  );

  // Draw-tool cursor is a ring matching the actual stroke width, scaled by zoom to
  // match its on-screen size (the canvas's internal resolution never changes with
  // zoom — only its displayed size does, by exactly the zoom factor).
  const toolCursor = useMemo(() => {
    if (tool !== 'brush' && tool !== 'pencil' && tool !== 'eraser') return undefined;
    const strokePx = strokeWidthForTool(tool, brushSize, eraserSize);
    return buildBrushCursor(strokePx * zoom);
  }, [tool, brushSize, eraserSize, zoom]);

  const handlePointerDown = useCallback(
    (e) => {
      if (!ready || interactionLocked) return;
      // While zoomed in, a one-finger touch drag is the only way to pan on
      // mobile (no preventDefault below, no pointer capture) — let it fall
      // through to the browser's native touch-scroll on canvas-stage-scroll
      // instead of starting a stroke. Mouse/pen still draw normally even when
      // zoomed, since desktop panning already works via scroll/trackpad.
      if (zoom !== 1 && e.pointerType === 'touch') return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      e.preventDefault();
      const { x, y } = canvasCoords(e);

      if (tool === 'fill') {
        applyFill(e);
        return;
      }

      canvas.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      lastPointRef.current = { x, y };

      const ctx = canvas.getContext('2d');
      ctx.save();
      applyStrokeStyle(ctx, tool);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();
    },
    [ready, interactionLocked, tool, zoom, canvasCoords, applyFill, applyStrokeStyle]
  );

  const handlePointerMove = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (tool === 'fill') return;
      if (!drawingRef.current || !ready || interactionLocked) return;
      e.preventDefault();
      const { x, y } = canvasCoords(e);
      const ctx = canvas.getContext('2d');
      const last = lastPointRef.current;
      ctx.save();
      applyStrokeStyle(ctx, tool);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();
      lastPointRef.current = { x, y };
    },
    [ready, interactionLocked, tool, canvasCoords, applyStrokeStyle]
  );

  const endStroke = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    commitState();
  }, [commitState]);

  const handlePointerUp = useCallback(
    (e) => {
      endStroke();
      try {
        canvasRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [endStroke]
  );

  const handlePointerLeave = useCallback(() => {
    endStroke();
  }, [endStroke]);

  const buildCompositeBlob = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img?.complete) return Promise.reject(new Error('Canvas not ready'));
    return new Promise((resolve, reject) => {
      const w = canvas.width;
      const h = canvas.height;
      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx = out.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to create export context'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      ctx.drawImage(canvas, 0, 0);
      try {
        out.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to export canvas'));
        }, 'image/png');
      } catch (e) {
        reject(
          new Error('Image export blocked by S3 CORS. Please allow GET origin for this app.')
        );
      }
    });
  }, []);

  // historyUi is a dummy counter: the real undo/redo state lives in refs (mutated
  // outside React's render cycle), so it's the dependency that forces recompute.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const undoDisabled = useMemo(() => historyIndexRef.current <= 0, [historyUi]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const redoDisabled = useMemo(() => historyIndexRef.current >= historyRef.current.length - 1, [historyUi]);

  // ---- Submission + leaderboard/report continuity ----
  // Accepts an optional puzzleId override for the one call site (a late joiner whose
  // session-wide drawing timer has already expired) that runs before the `puzzleId`
  // state set earlier in the same effect has actually committed/re-rendered — reading
  // the `puzzleId` state there would still see the stale pre-assignment value.
  const saveGameReport = useCallback(async (puzzleIdOverride) => {
    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your progress. Please sign in again.');
      return false;
    }

    const effectivePuzzleId = puzzleIdOverride ?? puzzleId;
    const finalPoints = PARTICIPATION_POINTS;
    const elapsedSeconds = getElapsedSeconds();
    const canvasAnswer = JSON.stringify({
      completed: true,
      puzzle_id: effectivePuzzleId != null ? Number(effectivePuzzleId) : null,
    });

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Collaborative Canvas',
        totalPuzzles:
          themeData?.total_puzzles ?? themeData?.puzzle_parts ?? sliceUrls.length ?? 9,
        currentTheme,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const reportTimes = buildCumulativeStageTimes(stageData, elapsedSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        status: 'completed',
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: canvasAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      // The external report used to be fired and forgotten: a rejected call was
      // never seen and never retried. It is now awaited (retrying network / 5xx
      // failures with jitter, bounded so a slow service cannot hold the player on
      // the canvas) and a final failure is logged. The local completion above is
      // already saved, so it is not turned into a player-facing error.
      try {
        await Promise.race([
          withRetry(
            () =>
              sendReport({
                token,
                userId,
                email: user?.email,
                points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
                accumulatePoints: false,
                time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
                name: user?.name,
                userType: user?.role || user?.source || 'ORG_USER',
                reportId: user?.reportId || '',
                themeName: 'Collaborative Canvas',
                // No report id means the entry made at login was missed: this call then CREATES it, so it
                // must carry the same identifying fields as the login entry (it used to send none of them).
                ...(user?.reportId ? {} : buildReportIdentity(user)),
              }),
            { maxAttempts: 3, backoffMs: 400 }
          ),
          new Promise((_, reject) => {
            window.setTimeout(() => reject(new Error('External report timed out')), 10000);
          }),
        ]);
      } catch (reportErr) {
        console.warn('External report was not delivered:', reportErr?.message || reportErr);
      }

      return true;
    } catch (err) {
      setMessage(err?.message || 'Failed to save your progress.');
      return false;
    }
  }, [adminToken, user, puzzleId, getElapsedSeconds, themeData, sliceUrls.length, currentTheme]);

  saveGameReportRef.current = saveGameReport;

  const handleSubmit = useCallback(
    async (options = {}) => {
      const auto = Boolean(options.auto);
      if (completingRef.current) return;
      // Normally a second call is a no-op (one tile upload per player). The one
      // exception: the tile image uploaded fine but saveGameReport() failed
      // afterward — retrying must skip straight back to that step below rather
      // than attempting to re-upload (the backend rejects a second upload for
      // an already-submitted tile with 409).
      const retrying =
        reportSaveFailed && (tileUploadedRef.current || retryModeRef.current === 'blank');
      if (submitOnceRef.current && !retrying) return;

      if (
        retryModeRef.current === 'blank' ||
        (auto && historyIndexRef.current <= 0 && !hasRestoredSnapshotRef.current)
      ) {
        // Nothing drawn — per product decision, skip the tile image upload for
        // this player; the auto-fill (Phase 5, via finalizeCanvasActivity) will
        // cover this tile from the master image instead. The player's own game
        // report still needs to be marked complete and they still need to move
        // on to the reveal page, same as anyone who did submit a drawing —
        // otherwise they're stuck staring at a locked canvas forever. (A restored
        // snapshot also sits at history index 0, but hasRestoredSnapshotRef tells
        // it apart from a genuinely untouched canvas, so a refresh right at the
        // wire doesn't get treated as "nothing drawn" and lose real work.)
        setMessage('Time is up. Since nothing was drawn, this tile will be auto-filled.');
        submitOnceRef.current = true;
        completingRef.current = true;
        setSubmitting(true);
        let outcome = null;
        try {
          // R2-N01: finalize (auto-fill) first, then save progress. Navigate
          // ONLY if both succeeded — a failure leaves a Retry button instead.
          outcome = await runBlankCompletionRef.current?.();
        } finally {
          setSubmitting(false);
        }
        if (outcome?.ok) {
          if (snapshotKeyRef.current) clearCanvasSnapshot(snapshotKeyRef.current);
          setSubmitted(true);
          navigate('/canvas-reveal', { replace: true });
        } else {
          completingRef.current = false;
        }
        return;
      }

      const userId = user?.userId;
      const organizationId = user?.organizationId;
      const sessionId = user?.sessionId;
      const token =
        adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

      if (!token || !userId || !organizationId || !sessionId) {
        setMessage('Unable to submit your canvas. Please sign in again.');
        return;
      }

      submitOnceRef.current = true;
      completingRef.current = true;
      setSubmitting(true);
      setMessage('');

      try {
        if (!tileUploadedRef.current) {
          const blob = await buildCompositeBlob();
          const resp = await submitCanvasTile({ token, userId, sessionId, organizationId, file: blob });
          tileUploadedRef.current = true;

          const { sessionId: fbSessionId, userId: fbUserId } = firebaseRef.current;
          if (fbSessionId && fbUserId) {
            // Written regardless of game mode — the final reveal board (both
            // modes) reads tileImageKey from here to show the actual painted
            // tile instead of falling back to the blank/master image.
            patchUserCanvasState(fbSessionId, fbUserId, {
              status: 'submitted',
              tileImageKey: resp?.tileImageKey,
            }).catch((err) => console.warn('Failed to patch canvas state:', err));
            // The finished image replaces the in-progress preview.
            clearLivePreview(fbSessionId, fbUserId).catch(() => {});
          }
        }

        const reportSaved = await saveGameReport();
        if (!reportSaved) {
          // The tile image is already uploaded and can't be retried — only
          // the completion record is missing. Previously this was ignored:
          // the UI proceeded straight to the reveal page as if everything had
          // succeeded, leaving an incomplete report with no way back to retry
          // (a second Submit was a no-op, and a second upload would 409).
          setReportSaveFailed(true);
          retryModeRef.current = 'complete';
          completingRef.current = false;
          setMessage(
            'Your artwork was saved, but we could not record your completion. Tap Retry to try again.'
          );
          return;
        }

        retryModeRef.current = null;
        setReportSaveFailed(false);
        if (snapshotKeyRef.current) clearCanvasSnapshot(snapshotKeyRef.current);
        setSubmitted(true);
        navigate('/canvas-reveal', { replace: true });
      } catch (err) {
        if (err?.tileFinalized) {
          // The tile was already finalized on the server (usually auto-filled
          // because the timer ended before this upload landed). Nothing to
          // resubmit: record completion and move on to the reveal page.
          tileUploadedRef.current = true;
          submitOnceRef.current = true;
          try {
            const saved = await saveGameReport();
            if (saved) {
              retryModeRef.current = null;
              setReportSaveFailed(false);
              if (snapshotKeyRef.current) clearCanvasSnapshot(snapshotKeyRef.current);
              setSubmitted(true);
              navigate('/canvas-reveal', { replace: true });
              return;
            }
          } catch (saveErr) {
            console.warn('Failed to record completion after finalized tile:', saveErr);
          }
          setReportSaveFailed(true);
          retryModeRef.current = 'complete';
          completingRef.current = false;
          setMessage('Time is up. We could not record your completion. Tap Retry to try again.');
          return;
        }
        if (!tileUploadedRef.current) submitOnceRef.current = false;
        completingRef.current = false;
        setMessage(err?.message || 'Failed to submit your canvas.');
      } finally {
        setSubmitting(false);
      }
    },
    [adminToken, user, buildCompositeBlob, saveGameReport, navigate, gameMode, reportSaveFailed]
  );

  handleSubmitRef.current = handleSubmit;

  // Finalizes the WHOLE session (not just this player's tile): marks every
  // still-unsubmitted game row as auto_filled from the theme's coloured
  // master image, then fans the result out to Firebase so every client's
  // LiveCanvasBoard updates. Safe to call from multiple clients whose Live
  // timers expire near-simultaneously — the backend endpoint is idempotent
  // (a repeat call finds no pending rows and returns an empty list), so only
  // the first caller's response actually does anything. Runs independently
  // of (and must never block or fail) this player's own submit flow above.
  const finalizeCanvasActivity = useCallback(async () => {
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken || (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);
    if (!token || !organizationId || !sessionId || !currentTheme) {
      throw new Error('Unable to finish the activity. Please sign in again.');
    }

    // The server only finalizes from ITS clock (expiry + a short grace), so an
    // early call answers ACTIVITY_NOT_EXPIRED with retryAfterSeconds; withRetry
    // waits that long. Transient network / 5xx failures are retried too.
    const result = await withRetry(() =>
      endCanvasActivity({ token, userId: user?.userId, organizationId, sessionId, currentTheme })
    );
    const autoFilledTiles = Array.isArray(result?.autoFilledTiles) ? result.autoFilledTiles : [];
    if (autoFilledTiles.length === 0) return result;

    const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);
    await Promise.all(
      autoFilledTiles.map((tile) =>
        patchUserCanvasState(firebaseSessionId, sanitizeFirebaseKey(tile.userid), {
          status: 'auto_filled',
          tileImageKey: tile.tile_s3_key,
        }).catch((err) => console.warn('Failed to patch canvas state for auto-filled tile:', err))
      )
    );
    return result;
  }, [adminToken, user, currentTheme]);

  const finalizeCanvasActivityRef = useRef(null);
  finalizeCanvasActivityRef.current = finalizeCanvasActivity;

  // Simultaneous expiry callbacks (timer effect + blank-canvas flow + retries)
  // share ONE in-flight finalize request; a failure clears it so Retry re-runs.
  const runFinalize = useCallback(() => {
    if (!finalizeFlightRef.current) {
      finalizeFlightRef.current = createSingleFlight(() => finalizeCanvasActivityRef.current());
    }
    return finalizeFlightRef.current();
  }, []);

  // R2-N01: for a player who drew nothing, their tile only becomes 'auto_filled'
  // once the session is finalized, and the server refuses to complete a player
  // whose tile is not finalized — so finalize first, save progress second.
  const runBlankCompletion = useCallback(
    async (puzzleIdOverride) => {
      const outcome = await finalizeThenComplete({
        finalize: () => runFinalize(),
        completeReport: () => saveGameReportRef.current?.(puzzleIdOverride),
      });
      if (outcome.ok) {
        retryModeRef.current = null;
        setReportSaveFailed(false);
      } else {
        retryModeRef.current = 'blank';
        setReportSaveFailed(true);
        setMessage(
          outcome.stage === 'finalize'
            ? 'Time is up, but we could not finalize your tile yet. Tap Retry to try again.'
            : 'Your tile was finalized, but we could not record your completion. Tap Retry to try again.'
        );
      }
      return outcome;
    },
    [runFinalize]
  );

  runBlankCompletionRef.current = runBlankCompletion;

  const handleTimerExpired = useCallback(() => {
    if (autoSubmitRef.current) return;
    autoSubmitRef.current = true;
    // Every browser's timer ends at the same instant. Starting the submit chain (a
    // PNG upload + several API calls) in the same millisecond on ~2,000 devices is
    // what overloads the server, so each client waits a small random time first. The
    // spread stays inside the server's grace window (see expirySpreadMs), so a real
    // drawing still lands BEFORE any finalize is trusted.
    const delay = randomDelayMs(expirySpreadMsRef.current);
    window.setTimeout(() => {
      // The drawing is auto-submitted FIRST; only then is the rest of the session finalized, so this
      // player's own finalize can never auto-fill their tile while their upload is still on its way.
      // (The blank-canvas path inside handleSubmit awaits this same single-flight finalize.)
      submitThenFinalize({
        submit: () => handleSubmitRef.current?.({ auto: true }),
        finalize: () => runFinalize(),
        onError: (err, step) =>
          console.warn(`Time-up ${step} failed:`, err?.message || err),
      });
    }, delay);
  }, [runFinalize]);

  handleTimerExpiredRef.current = handleTimerExpired;

  useEffect(() => {
    // `ready` (outline image loaded + any saved snapshot already restored) is
    // required here, not just timerReady — without it, a canvas that loads
    // already-expired (see fetchCanvasData) could fire the auto-submit before
    // restoreSavedDrawing has actually run, submitting a still-blank canvas
    // instead of the player's just-restored work.
    if (loading || gameMode !== 'live' || !timerReady || !timerExpired || !ready) return;
    handleTimerExpiredRef.current?.();
  }, [loading, gameMode, timerReady, timerExpired, ready]);

  const onSubmitClick = useCallback(() => {
    handleSubmit({ auto: false });
  }, [handleSubmit]);

  if (loading) {
    return (
      <div className="canvas-page" style={pageStyle}>
        <p className="canvas-empty">Loading your canvas…</p>
      </div>
    );
  }

  if (!outlineUrl) {
    return (
      <div className="canvas-page" style={pageStyle}>
        <p className="canvas-empty">
          {message || 'Canvas is not configured. Please try again.'}
        </p>
        {sessionEnded ? (
          <button
            type="button"
            className="canvas-action-btn canvas-action-btn--submit"
            onClick={() => navigate('/canvas-reveal', { replace: true })}
          >
            View the finished canvas
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="canvas-page" style={pageStyle}>
      <div className="canvas-heading-wrap">
        <h1 className="canvas-heading">{title}</h1>
        <button
          type="button"
          className="canvas-rules-fab"
          onClick={() => setShowRulesModal(true)}
          title="How to play"
          aria-label="How to play"
        >
          ?
        </button>
      </div>

      {showRulesModal ? (
        <div
          className="canvas-rules-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="How to play"
          onClick={() => setShowRulesModal(false)}
        >
          <div
            className="canvas-rules-modal"
            style={rulesModalBg ? { '--rules-bg-image': `url(${rulesModalBg})` } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {rulesModalBg ? <div className="canvas-rules-modal-bg" aria-hidden="true" /> : null}
            <div className="canvas-rules-modal-scrim">
              <button
                type="button"
                className="canvas-rules-modal-close"
                onClick={() => setShowRulesModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
              <h2 className="canvas-rules-modal-heading">How to Play</h2>
              {rules.length > 0 ? (
                <ul className="canvas-rules-modal-list">
                  {rules.map((rule, i) => (
                    <li key={`rule-${i}`}>
                      <span className="canvas-rules-modal-check" aria-hidden="true">
                        &#10003;
                      </span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="canvas-rules-modal-empty">No rules available at this time.</p>
              )}
              <button
                type="button"
                className="canvas-rules-modal-gotit"
                onClick={() => setShowRulesModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="canvas-columns">
        <section className="canvas-left-col">
          <div className="canvas-panel-header">
            <h2 className="canvas-panel-title">Your Canvas</h2>
          </div>

          <div className="canvas-workspace">
            <aside className="canvas-toolbar" role="toolbar" aria-label="Drawing tools">
              <button
                type="button"
                className={`canvas-tool-btn${tool === 'brush' ? ' canvas-tool-btn--active' : ''}`}
                onClick={() => setTool('brush')}
                title="Brush — smooth, adjustable-size strokes for painting larger areas"
                aria-label="Brush — smooth, adjustable-size strokes for painting larger areas"
                aria-pressed={tool === 'brush'}
              >
                <BrushIcon />
                <span className="canvas-tool-btn-label">Brush</span>
              </button>
              <button
                type="button"
                className={`canvas-tool-btn${tool === 'pencil' ? ' canvas-tool-btn--active' : ''}`}
                onClick={() => setTool('pencil')}
                title="Pencil — thin, semi-transparent strokes for fine detail and outlines"
                aria-label="Pencil — thin, semi-transparent strokes for fine detail and outlines"
                aria-pressed={tool === 'pencil'}
              >
                <PencilIcon />
                <span className="canvas-tool-btn-label">Pencil</span>
              </button>
              <button
                type="button"
                className={`canvas-tool-btn${tool === 'eraser' ? ' canvas-tool-btn--active' : ''}`}
                onClick={() => setTool('eraser')}
                title="Eraser — remove color and strokes you've drawn"
                aria-label="Eraser — remove color and strokes you've drawn"
                aria-pressed={tool === 'eraser'}
              >
                <EraserIcon />
                <span className="canvas-tool-btn-label">Eraser</span>
              </button>
              <button
                type="button"
                className={`canvas-tool-btn${tool === 'fill' ? ' canvas-tool-btn--active' : ''}`}
                onClick={() => setTool('fill')}
                title="Fill — tap an enclosed area to flood it with the selected color"
                aria-label="Fill — tap an enclosed area to flood it with the selected color"
                aria-pressed={tool === 'fill'}
              >
                <FillIcon />
                <span className="canvas-tool-btn-label">Fill</span>
              </button>
              <button
                type="button"
                className={`canvas-tool-btn${showColorPicker ? ' canvas-tool-btn--active' : ''}`}
                onClick={() => setShowColorPicker((v) => !v)}
                title="Colour — choose the paint color for the Brush, Pencil, and Fill tools"
                aria-label="Colour — choose the paint color for the Brush, Pencil, and Fill tools"
                aria-pressed={showColorPicker}
              >
                <PaletteIcon />
                <span className="canvas-tool-btn-label">Colour</span>
              </button>
            </aside>

            <div className="canvas-stage-shell">
              <div
                className={`canvas-stage-scroll${zoom !== 1 ? ' canvas-stage-scroll--zoomed' : ''}`}
                ref={wrapRef}
              >
                <div className={`canvas-stage-stack${locked ? ' canvas-stage-stack--locked' : ''}`}>
                  <img
                    ref={imgRef}
                    src={outlineUrl}
                    crossOrigin="anonymous"
                    alt="Your tile outline"
                    draggable={false}
                    className="canvas-base-img"
                    onLoad={handleBaseImgLoad}
                    onError={handleBaseImgError}
                    style={baseSize ? { width: baseSize.cw * zoom, height: baseSize.ch * zoom } : undefined}
                  />
                  <canvas
                    ref={canvasRef}
                    className="canvas-draw-layer"
                    style={{
                      ...(baseSize
                        ? { width: baseSize.cw * zoom, height: baseSize.ch * zoom }
                        : { width: 0, height: 0 }),
                      ...(toolCursor ? { cursor: toolCursor } : null),
                      // The CSS class defaults to touch-action:none so a drawing
                      // stroke's touchmove never gets hijacked into a page
                      // scroll. But that's exactly what blocks one-finger pan
                      // on mobile once zoomed in (touch-action is decided by
                      // the browser before any JS runs, so handlePointerDown's
                      // early-return for touch+zoom isn't enough on its own) —
                      // allow native panning here instead while zoomed.
                      touchAction: zoom !== 1 ? 'pan-x pan-y' : 'none',
                    }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onPointerLeave={handlePointerLeave}
                  />
                  {!ready ? <p className="canvas-stage-loading">Loading your canvas…</p> : null}
                  {locked ? (
                    <div className="canvas-locked-overlay">
                      <p>Time&apos;s up</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* A normal flex sibling, not floated over the stage — so it never sits
                on top of the artwork no matter how the image happens to fit. */}
            <div className="canvas-zoom-controls" role="group" aria-label="Zoom">
              <button
                type="button"
                className="canvas-zoom-btn"
                onClick={handleZoomOut}
                disabled={zoom <= ZOOM_MIN}
                title="Zoom out"
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="canvas-zoom-level"
                onClick={handleZoomReset}
                title="Reset zoom"
                aria-label="Reset zoom"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="canvas-zoom-btn"
                onClick={handleZoomIn}
                disabled={zoom >= ZOOM_MAX}
                title="Zoom in"
                aria-label="Zoom in"
              >
                +
              </button>
            </div>
          </div>

          <div className="canvas-controls">
            {tool === 'brush' ? (
              <label className="canvas-size-row">
                <span className="canvas-size-label">Brush Size</span>
                <input
                  type="range"
                  min={1}
                  max={BRUSH_SIZE_MAX}
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  aria-label="Brush size"
                />
                <span className="canvas-size-value">{brushSize}px</span>
              </label>
            ) : null}
            {tool === 'eraser' ? (
              <label className="canvas-size-row">
                <span className="canvas-size-label">Eraser Size</span>
                <input
                  type="range"
                  min={4}
                  max={ERASER_SIZE_MAX}
                  value={eraserSize}
                  onChange={(e) => setEraserSize(Number(e.target.value))}
                  aria-label="Eraser size"
                />
                <span className="canvas-size-value">{eraserSize}px</span>
              </label>
            ) : null}

            <ColorSwatchRow value={color} onChange={setColor} large />

            <div className="canvas-action-row">
              <button
                type="button"
                className="canvas-action-btn"
                onClick={handleUndo}
                disabled={undoDisabled || interactionLocked}
                title="Undo — step back to before your last stroke or fill"
              >
                Undo
              </button>
              <button
                type="button"
                className="canvas-action-btn"
                onClick={handleRedo}
                disabled={redoDisabled || interactionLocked}
                title="Redo — reapply the stroke or fill you just undid"
              >
                Redo
              </button>
              <button
                type="button"
                className="canvas-action-btn canvas-action-btn--submit"
                onClick={onSubmitClick}
                disabled={!ready || (interactionLocked && !reportSaveFailed)}
                title={
                  reportSaveFailed
                    ? 'Retry — your artwork is saved, retry recording completion'
                    : 'Submit — finish and hand in your tile'
                }
              >
                {submitting
                  ? 'Submitting…'
                  : reportSaveFailed
                    ? 'Retry'
                    : submitted
                      ? 'Submitted'
                      : 'Submit'}
              </button>
            </div>
          </div>

          {message ? <p className="canvas-message">{message}</p> : null}
        </section>

        {gameMode === 'live' ? (
          <section className="canvas-right-col">
            <div className="canvas-panel-header">
              <h2 className="canvas-panel-title">Live Collaborative Artwork</h2>
              <div className="canvas-timer-badge" aria-label="Stage timer" aria-live="polite">
                <ClockIcon />
                <span className="canvas-timer-value">{countdownDisplay}</span>
              </div>
            </div>

            <LiveCanvasBoard
              sessionId={user?.sessionId}
              organizationId={user?.organizationId}
              outlineUrls={sliceUrls}
              currentUserId={user?.userId ? String(user.userId) : null}
              macroRows={macroRows}
              macroCols={macroCols}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
}
